import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useNotification } from "../../../providers/NotificationProvider";
import { BSC_CATEGORY_CONFIG } from "./bscConfig";

const formatDate = (iso) => {
  if (!iso) return "—";
  return iso.substring(0, 10).replace(/-/g, "/");
};

export default function BscGoalListPage() {
  const { businessDomainId, businessUnitId, category } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const config = BSC_CATEGORY_CONFIG[category];

  const loadData = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    try {
      const data = await config.fetchGoals(businessUnitId);
      setItems(Array.isArray(data) ? data.map((r, i) => ({ id: i, ...r })) : []);
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, config]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, [loadData]);

  const backPath = `/strategy/${businessDomainId}/business-units/${businessUnitId}`;
  const title = config ? t(config.titleKey) : category;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(backPath)}
          variant="text"
        >
          {t("common.back")}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Typography color="text.secondary">{t("common.noData")}</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 600 }}>{t("bsc.successFactor")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("bsc.indicator")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("bsc.unit")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("bsc.plannedValue")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("bsc.plannedDate")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("bsc.deadline")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate(
                      `/strategy/${businessDomainId}/business-units/${businessUnitId}/bsc/${category}/${row.id}`
                    )
                  }
                >
                  <TableCell>{row.successFactorName || "—"}</TableCell>
                  <TableCell>{row.indicator?.indicatorName || "—"}</TableCell>
                  <TableCell>{row.indicator?.unit || "—"}</TableCell>
                  <TableCell>{row.goal?.plannedValue ?? "—"}</TableCell>
                  <TableCell>{formatDate(row.goal?.plannedDate)}</TableCell>
                  <TableCell>{formatDate(row.goal?.deadline)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
